/**
 * GSB API Service
 */
import { HttpCallRequest } from '../types/requests';
import { GSB_CONFIG, getGsbLangCode } from '../config/gsb-config';
import { AuthService } from '../services';
import { GsbBulkRequest, GsbBulkCallRequest } from '../types/requests';
import { GsbBulkResponse } from '../types/responses';
import { GsbUtils } from '../utils';

// Store a singleton instance
let apiServiceInstance: GsbApiService | null = null;

// Bulk operation interfaces
interface PendingBulkCall {
    request: HttpCallRequest;
    endPoint: string;
    token?: string;
    tenantCode?: string;
    resolve: (value: any) => void;
    reject: (error: any) => void;
    reqId: string;
}

interface BulkBatch {
    calls: PendingBulkCall[];
    timer: ReturnType<typeof setTimeout>;
    tenantCode?: string;
    token?: string;
}

export class GsbApiService {
    // We'll use a simpler structure with only one base URL that changes based on tenant
    private pendingBulkCalls: Map<string, BulkBatch> = new Map();
    private bulkCallCounter: number = 0;

    constructor() {
    }

    /**
     * Get the singleton instance of the service
     */
    public static getInstance(): GsbApiService {
        if (!apiServiceInstance) {
            apiServiceInstance = new GsbApiService();
        }
        return apiServiceInstance;
    }

    private getBaseUrl(tenantCode?: string, baseDomain?: string): string {
        return GSB_CONFIG.API_URL(tenantCode, baseDomain);
    }

 
    /**
     * Extract tenant code from a JWT token
     * @param token The JWT token
     * @returns The tenant code or undefined
     */
    private extractTenantCode(token: string): string | undefined {
        return GSB_CONFIG.extractTenantCode(token);
    }



    /**
     * Generate a unique request ID for bulk operations
     */
    private generateReqId(): string {
        return `req_${Date.now()}_${++this.bulkCallCounter}`;
    }

    /**
     * Get the bulk batch key for grouping requests
     */
    private getBulkBatchKey(tenantCode?: string, token?: string): string {
        return `${tenantCode || 'default'}_${token || 'noauth'}`;
    }

    /**
     * Ensure request has a unique ID for bulk operations
     */
    private ensureRequestId(request: HttpCallRequest): void {
        if (request.content && typeof request.content === 'object') {
            // Check if the request content doesn't have an id
            if (!request.content.id) {
                request.content.id = GsbUtils.newId();
            }
        }
    }

    /**
     * Execute a bulk batch
     */
    private async executeBulkBatch(batchKey: string): Promise<void> {
        const batch = this.pendingBulkCalls.get(batchKey);
        if (!batch || batch.calls.length === 0) {
            return;
        }

        // Remove the batch from pending calls
        this.pendingBulkCalls.delete(batchKey);

        try {
            // Prepare bulk request - use request.id as reqId for proper response matching
            const bulkCalls: GsbBulkCallRequest[] = batch.calls.map(call => ({
                endPoint: call.endPoint,
                request: call.request.content,
                reqId: call.request.content?.id || call.reqId // Use request.id if available, fallback to generated reqId
            }));

            const bulkRequest: GsbBulkRequest = {
                bulkCalls,
                variation: {
                    tenantCode: batch.tenantCode || 'default',
                    langCode: getGsbLangCode()
                }
            };

            // Execute bulk request
            const bulkResponse = await this.httpCallAsync({
                method: 'POST',
                protocol: 'https',
                hostName: 'api',
                content: bulkRequest,
                path: '/api/entity/bulk',
                bearerToken: batch.token,
                contentType: 'application/json',
                tenantCode: batch.tenantCode
            });

            // Process individual responses
            const bulkResult = bulkResponse as GsbBulkResponse;
            if (bulkResult.results) {
                // Create a map for quick lookup using reqId (which should match request.id)
                const resultMap = new Map<string, any>();
                for (const result of bulkResult.results) {
                    if (result.reqId && result.response) {
                        try {
                            resultMap.set(result.reqId, JSON.parse(result.response));
                        } catch (e) {
                            resultMap.set(result.reqId, result.response);
                        }
                    }
                }

                // Resolve individual promises using the request.id to match responses
                for (const call of batch.calls) {
                    const lookupId = call.request.content?.id || call.reqId;
                    const result = resultMap.get(lookupId);
                    if (result !== undefined) {
                        call.resolve(result);
                    } else {
                        call.reject(new Error(`No result found for request ID ${lookupId}`));
                    }
                }
            } else {
                // If no results, reject all calls
                for (const call of batch.calls) {
                    call.reject(new Error('Bulk operation failed: No results returned'));
                }
            }
        } catch (error) {
            // If bulk operation fails, reject all individual calls
            for (const call of batch.calls) {
                call.reject(error);
            }
        }
    }

    /**
     * Add a call to the bulk batch
     */
    private addToBulkBatch(
        request: HttpCallRequest,
        endPoint: string,
        token?: string,
        tenantCode?: string
    ): Promise<any> {
        return new Promise((resolve, reject) => {
            // Ensure the request has a unique ID for tracking
            this.ensureRequestId(request);
            
            // Use the request.id as the reqId for proper response matching
            const reqId = request.content?.id || this.generateReqId();
            const batchKey = this.getBulkBatchKey(tenantCode, token);

            const pendingCall: PendingBulkCall = {
                request,
                endPoint,
                token,
                tenantCode,
                resolve,
                reject,
                reqId
            };

            // Get or create batch
            let batch = this.pendingBulkCalls.get(batchKey);
            if (!batch) {
                batch = {
                    calls: [],
                    timer: setTimeout(() => {
                        this.executeBulkBatch(batchKey);
                    }, 50), // 50ms delay
                    tenantCode,
                    token
                };
                this.pendingBulkCalls.set(batchKey, batch);
            } else {
                // Clear existing timer and set a new one
                clearTimeout(batch.timer);
                batch.timer = setTimeout(() => {
                    this.executeBulkBatch(batchKey);
                }, 50);
            }

            // Add call to batch
            batch.calls.push(pendingCall);
        });
    }

    /**
     * Make an HTTP call
     */
    async httpCall(dto: any, callback?: (response: any) => void, errorCallback?: (error: any) => void) {
        try {
            const response = await this.httpCallAsync(dto);
            if (callback) {
                callback(response);
            }
            return response;
        } catch (error) {
            if (errorCallback) {
                errorCallback(error);
            }
            throw error;
        }
    }

    /**
     * Execute HTTP call asynchronously
     */
    async httpCallAsync(dto: any) {

        const requestData = this.convertToRequestData(dto);
        try {
            const response = await fetch(requestData.url, requestData.init);

            if (!response.ok) {
                throw response;
            }

            const data = await response.json();
            return data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Convert request parameters to fetch request data
     */
    convertToRequestData(requestParameters: any) {
        const url = new URL(requestParameters.path || '', this.getBaseUrl(requestParameters.tenantCode, requestParameters.baseDomain)).toString();

        const init: RequestInit = {
            method: requestParameters.method,
            headers: {
                // Only add Authorization header if noAuth is not true and we have a bearerToken
                ...(!requestParameters.noAuth && requestParameters.bearerToken ? { 'Authorization': `Bearer ${requestParameters.bearerToken}` } : {}),
                ...requestParameters.headers
            }
        };

        if(!requestParameters.content)
            requestParameters.content = {};

        // Handle FormData requests
        if (requestParameters.isFormData) {
            const formData = new FormData();
            
            // Convert JSON content to FormData
            for (const [key, value] of Object.entries(requestParameters.content)) {
                if (value instanceof File || value instanceof Blob) {
                    // Handle File/Blob objects
                    formData.append(key, value, (value as File).name);
                } else if (typeof value === 'object' && value !== null) {
                    // Handle objects by JSON stringifying them
                    formData.append(key, JSON.stringify(value));
                } else if (value !== undefined && value !== null) {
                    // Handle primitive values
                    formData.append(key, String(value));
                }
            }
            
            init.body = formData;
            // Don't set Content-Type for FormData - let the browser set it with boundary
        } else {
            // Handle regular JSON requests
            init.headers = {
                ...init.headers,
                'Content-Type': requestParameters.contentType || 'application/json'
            };
            if (requestParameters.content) {
                init.body = JSON.stringify(requestParameters.content);
            }
        }

        return { url, init };
    }



    /**
     * Call the API with the appropriate token and tenant code
     */
    async callApi(req: HttpCallRequest & { useBulk?: boolean }, endPoint: string, token?: string, tenantCode?: string) {

        const authService = AuthService.getInstance();

        // Skip token logic if noAuth is true
        let bearerToken: string | undefined = undefined;
        if (!req.noAuth) {
            // If no token is provided, get the appropriate token based on the endpoint
            if (!token) {
                token = authService.getToken() || undefined;
            }
            
            bearerToken = token;
        } 

        // If no tenant code is provided and we have a token, extract it from the token
        if (!tenantCode && token) {
            tenantCode = this.extractTenantCode(token);
        }

        // If still no tenant code, get it from config
        if (!tenantCode) {
            tenantCode = authService.getTenantCode();
        }

        if(tenantCode && req.content && !req.content?.variation?.tenantCode) {
            if(!req.content.variation) {
                req.content.variation = {};
            }
            req.content.variation.tenantCode = tenantCode;
        }

        if(tenantCode && req.content && !req.content?.variation?.langCode) {
            if(!req.content.variation) {
                req.content.variation = {};
            }
            req.content.variation.langCode = getGsbLangCode();
        }
        // Prepare the request
        const request: HttpCallRequest = {
            ...req,
            path: endPoint,
            bearerToken: bearerToken,
            contentType: req.contentType || 'application/json',
            jsonResponse: req.jsonResponse !== undefined ? req.jsonResponse : true,
            tenantCode: tenantCode
        };

        // Check if we should use bulk operations
        const useBulk = req.useBulk !== false;

        if (useBulk) {
            // Add to bulk batch
            return this.addToBulkBatch(request, endPoint, bearerToken, tenantCode);
        } else {
            // Execute immediately
            return this.httpCall(request);
        }
    }
}
